import React, { useRef } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { StyledLink } from "../../components/CustomMUI";
import MotionInView from "../../components/MotionInView";

// As the homepage's other section entrances (see Home.js)
const TRIGGER_MARGIN = "0px 0px -20% 0px";

/**
 * The homepage's mission statement and ways to support Curate, beside a
 * graphic of a hand holding up the app. The text fades and rises in; the hand
 * lifts up into frame from the section's bottom edge, tilting upright as it
 * settles, as if being raised to show you.
 */
export default function MissionSection() {
  const reduce = useReducedMotion();
  const handRef = useRef(null);
  const handInView = useInView(handRef, {
    once: true,
    margin: TRIGGER_MARGIN,
  });

  return (
    <Grid
      container
      spacing={{ xs: 3, md: 5 }}
      sx={{
        maxWidth: "lg",
        alignItems: "stretch",
        justifyContent: "flex-start",
        pt: { xs: 6, md: 0 },
      }}
    >
      <Grid
        item
        xs={12}
        md={8}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          my: { md: 4 },
        }}
      >
        {/* A wrapper rather than the Grid itself: Grid reads `margin` as a
            CSS margin, not MotionInView's trigger inset */}
        <MotionInView margin={TRIGGER_MARGIN} duration={0.6}>
          <Typography variant="h4" gutterBottom sx={{ textWrap: "balance" }}>
            <b>Enhancing curiosity, not exploiting attention</b>
          </Typography>
          <Typography gutterBottom>
            Curate is an ad-free space that is intentionally quiet, low-friction
            and non-addictive: a tool to enrich your interests, not compete for
            your time.
          </Typography>
          <Typography gutterBottom>
            Curate is still in its early stages, but it’s being built toward a
            bigger vision: a way to discover what to watch and read through
            genuine human taste, not algorithms or feeds.
          </Typography>
          <Typography gutterBottom>
            Curate is self-funded and solo-built. If you’d like to support its
            growth, you can:
          </Typography>
          <Box
            component="ul"
            sx={{
              pl: 3,
              m: 0,
              // listStyleType: "'–  '", // en-dash + space
            }}
          >
            <Typography component="li">
              <Typography component="span" fontWeight="bold">
                Spread the word
              </Typography>{" "}
              — personal recommendations are always the most powerful
            </Typography>
            <Typography component="li">
              <StyledLink
                href="https://app.youform.com/forms/fghdoj5a"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: (theme) => theme.palette.primary.main }}
              >
                <b>Leave feedback</b>
              </StyledLink>{" "}
              — your ideas directly shape what gets built next
            </Typography>
            <Typography component="li">
              <StyledLink
                href="https://ko-fi.com/curate"
                target="_blank"
                rel="noopener"
                sx={{ color: (theme) => theme.palette.primary.main }}
              >
                <b>Buy me a coffee</b>
              </StyledLink>{" "}
              — donations help me keep Curate independent and user-focused
            </Typography>
          </Box>
          <Typography sx={{ mt: 1 }}>
            Your support means a lot and makes a huge difference ☺️
          </Typography>
        </MotionInView>
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          mt: { xs: 0, md: 2 },
          // Clips the hand below the section's bottom edge as it lifts in
          overflow: "hidden",
        }}
      >
        <Box
          ref={handRef}
          component={motion.img}
          initial={reduce ? false : { opacity: 0, y: "40%", rotate: 8 }}
          animate={
            handInView || reduce ? { opacity: 1, y: 0, rotate: 0 } : undefined
          }
          transition={{
            type: "spring",
            stiffness: 70,
            damping: 14,
            opacity: { duration: 0.4 },
          }}
          // Pivots at the wrist, so the tilt reads as the arm straightening
          style={{ transformOrigin: "50% 100%" }}
          src={`/images/mission-graphic.png`}
          alt={
            "Hand holding the curate app in the air with movies, books and TV shows flowing out of it"
          }
          sx={{
            width: { xs: 250, md: "100%" },
            display: "block",
          }}
        />
      </Grid>
    </Grid>
  );
}
